import { useState, type CSSProperties } from "react";
import type { AnimationMode } from "@/lib/types";
import EscapingNoButton from "../EscapingNoButton";
import ConfettiOverlay from "./ConfettiOverlay";

/**
 * Botones Yes/No cuyo comportamiento depende del `animationMode`.
 *
 * Espejo web del `AnimationModeButtons` de iOS: centraliza TODA la lógica de
 * los modos que afectan a los CTA (`escapeNo`, `growYes`, `shakeNo`,
 * `swapButtons`, `lockedNo`, `confettiYes`). Los modos que no tocan los
 * botones caen en el `default`, con dos botones estáticos.
 *
 * Añadir un modo nuevo = un caso más en el `switch` + (si hace falta) un
 * componente variante. Nada más.
 */
export interface ModeButtonsProps {
  mode: AnimationMode;
  yesText: string;
  noText: string;
  onYes: () => void;
  onNo: () => void;
}

/** Props que reciben las variantes internas (no necesitan `mode`). */
interface ButtonsProps {
  yesText: string;
  noText: string;
  onYes: () => void;
  onNo: () => void;
}

export default function ModeButtons({ mode, yesText, noText, onYes, onNo }: ModeButtonsProps) {
  switch (mode) {
    case "escapeNo":
      return <EscapeButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    case "growYes":
      return <GrowYesButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    case "shakeNo":
      return <ShakeNoButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    case "swapButtons":
      return <SwapButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    case "lockedNo":
      return <LockedNoButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    case "confettiYes":
      return <ConfettiYesButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
    default:
      return <StaticButtons yesText={yesText} noText={noText} onYes={onYes} onNo={onNo} />;
  }
}

//MARK: - Primitivas compartidas

function YesButton({
  children,
  style,
  onClick
}: {
  children: React.ReactNode;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-primary invite-choice-button rounded-pill text-base font-semibold"
      style={style}
    >
      {children}
    </button>
  );
}

function NoButton({
  children,
  onClick,
  style,
  className = ""
}: {
  children: React.ReactNode;
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={`btn-secondary invite-choice-button rounded-pill text-base font-semibold ${className}`}
    >
      {children}
    </button>
  );
}

//MARK: - Variantes

/** `default`: dos botones estáticos. */
function StaticButtons({ yesText, noText, onYes, onNo }: ButtonsProps) {
  return (
    <div className="invite-button-row mt-8 flex flex-wrap items-start justify-center gap-4">
      <YesButton onClick={onYes}>{yesText}</YesButton>
      <NoButton onClick={onNo}>{noText}</NoButton>
    </div>
  );
}

/** `escapeNo`: el No huye. Reutiliza el `EscapingNoButton` existente. */
function EscapeButtons({ yesText, noText, onYes, onNo }: ButtonsProps) {
  return (
    <div className="invite-button-row mx-auto mt-8 flex w-fit max-w-full items-center justify-center gap-3 overflow-visible sm:gap-4">
      <YesButton onClick={onYes}>{yesText}</YesButton>
      <div className="relative grid min-h-12 min-w-[120px] place-items-center overflow-visible">
        <EscapingNoButton title={noText} onSelect={onNo} maxJump={260} boundsPadding={14} />
      </div>
    </div>
  );
}

/** `growYes`: cada toque en No agranda Yes y encoge No. */
function GrowYesButtons({ yesText, noText, onYes, onNo }: ButtonsProps) {
  const [steps, setSteps] = useState(0);
  const max = 5;
  const yesScale = 1 + steps * 0.08;
  const noScale = Math.max(1 - steps * 0.1, 0.55);

  return (
    <div className="invite-button-row mt-8 flex flex-wrap items-center justify-center gap-4">
      <YesButton
        onClick={onYes}
        style={{ transform: `scale(${yesScale})`, transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}
      >
        {yesText}
      </YesButton>
      <NoButton
        style={{
          transform: `scale(${noScale})`,
          opacity: steps >= max ? 0.3 : 1,
          transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease"
        }}
        onClick={() => {
          setSteps((s) => Math.min(s + 1, max));
          window.setTimeout(onNo, 180);
        }}
      >
        {noText}
      </NoButton>
    </div>
  );
}

/** `shakeNo`: el No tiembla y muestra error; solo Yes acepta. */
function ShakeNoButtons({ yesText, noText, onYes }: ButtonsProps) {
  const [shaking, setShaking] = useState(false);
  const [showError, setShowError] = useState(false);

  return (
    <div className="relative">
      <div className="invite-button-row mt-8 flex flex-wrap items-start justify-center gap-4">
        <YesButton onClick={onYes}>{yesText}</YesButton>
        <NoButton
          className={shaking ? "animate-shake" : ""}
          onClick={() => {
            setShaking(false);
            setShowError(false);
            requestAnimationFrame(() => {
              setShaking(true);
              setShowError(true);
            });
            setTimeout(() => setShaking(false), 520);
            setTimeout(() => setShowError(false), 2200);
          }}
        >
          {noText}
        </NoButton>
      </div>
      {showError && (
        <div className="pointer-events-none absolute left-1/2 top-full z-30 mt-3 w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-red-200/80 bg-white/90 px-4 py-3 text-left text-sm font-semibold text-red-800 shadow-[0_18px_45px_-28px_rgba(127,29,29,0.7)] backdrop-blur-xl">
          <div className="flex items-start gap-2">
            <span aria-hidden className="mt-0.5 grid h-5 w-5 place-items-center rounded-full bg-red-100 text-xs">!</span>
            <span>This option is temporarily unavailable. Please choose yes to continue.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** `swapButtons`: Yes y No intercambian posición al tocar No. */
function SwapButtons({ yesText, noText, onYes }: ButtonsProps) {
  const [swapped, setSwapped] = useState(false);

  const yes = <YesButton key="yes" onClick={onYes}>{yesText}</YesButton>;
  const no = (
    <NoButton
      key="no"
      onClick={() => {
        setSwapped((s) => !s);
      }}
    >
      {noText}
    </NoButton>
  );

  return (
    <div className="invite-button-row mt-8 flex flex-wrap items-start justify-center gap-4">
      {swapped ? (
        <>
          {no}
          {yes}
        </>
      ) : (
        <>
          {yes}
          {no}
        </>
      )}
    </div>
  );
}

/** `lockedNo`: el No aparece bloqueado/disabled — solo queda decir Yes. */
function LockedNoButtons({ yesText, noText, onYes }: ButtonsProps) {
  return (
    <div className="invite-button-row mt-8 flex flex-wrap items-start justify-center gap-4">
      <YesButton onClick={onYes}>{yesText}</YesButton>
      <NoButton
        // Sin onClick + cursor not-allowed + opacidad baja: affordance de
        // "no disponible". El candado refuerza el mensaje visualmente.
        className="cursor-not-allowed opacity-40"
        aria-disabled
      >
        <span className="inline-flex items-center gap-1.5">
          <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="10" width="14" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          {noText}
        </span>
      </NoButton>
    </div>
  );
}

/** `confettiYes`: botones estáticos + confeti al pulsar Yes. */
function ConfettiYesButtons({ yesText, noText, onYes, onNo }: ButtonsProps) {
  const [celebrate, setCelebrate] = useState(false);

  return (
    <>
      <div className="invite-button-row mt-8 flex flex-wrap items-start justify-center gap-4">
        <span onClick={() => {
          setCelebrate(true);
          window.setTimeout(onYes, 420);
        }}>
          <YesButton>{yesText}</YesButton>
        </span>
        <NoButton onClick={onNo}>{noText}</NoButton>
      </div>
      <ConfettiOverlay play={celebrate} />
    </>
  );
}
